import { IconShieldCheck, IconShieldOff } from "@tabler/icons-react";
import { useId, useState } from "react";

import { errorMessage } from "../../api/queries";
import {
  useEnrolSecondFactor,
  useRemoveSecondFactor,
  useVerifySecondFactor,
} from "../../api/queries";
import type { SecondFactor, SecondFactorEnrolment } from "../../api/types";
import { formatClock } from "../../lib/format";
import { Field } from "../ui/Field";

interface SecondFactorSectionProps {
  /** What the server says about this account's code. */
  secondFactor: SecondFactor;
  /**
   * How long a confirmation lasts, when the console has published it.
   *
   * Null on the waiting-for-approval screen: a `pending` account may set its
   * code up but may not read `GET /config`, and the console does not print a
   * number it was not given.
   */
  stepUpMinutes?: number | null;
}

/** The codes, shown once, with the sentence that says they will not come back. */
function RecoveryCodes({
  enrolment,
}: {
  enrolment: SecondFactorEnrolment;
}): JSX.Element {
  return (
    <div className="secret-block">
      <p className="secret-block__title">
        Ten one-off codes, for the day the phone is not there
      </p>
      <ul className="codes">
        {enrolment.recovery_codes.map((code) => (
          <li className="codes__item mono" key={code}>
            {code}
          </li>
        ))}
      </ul>
      <p className="form__note">
        Each one works once. This is the only time the console can show them —
        it keeps them scrambled and cannot read them back. Copy them somewhere
        only you can reach before you close this.
      </p>
    </div>
  );
}

/**
 * Your code: what it is, how to set it up, and what it is asked for.
 *
 * It is called *your code* on every screen, never "MFA" and never "2FA": those
 * words are what a security questionnaire asks about, and a Google sign-in
 * tells this console nothing about a second factor. What the console runs is
 * its own six digits, and this is where they are enrolled.
 *
 * Args:
 *     secondFactor: What the server publishes about this account's code.
 *     stepUpMinutes: How long a confirmation lasts, when it is published.
 *
 * Returns:
 *     The enrolment, or the state of the code already set up.
 */
export function SecondFactorSection({
  secondFactor,
  stepUpMinutes = null,
}: SecondFactorSectionProps): JSX.Element {
  const codeId = useId();
  const removeId = useId();
  const [enrolment, setEnrolment] = useState<SecondFactorEnrolment | null>(null);
  const [code, setCode] = useState("");
  const [removeCode, setRemoveCode] = useState("");
  const [removing, setRemoving] = useState(false);

  const enrol = useEnrolSecondFactor();
  const verify = useVerifySecondFactor();
  const remove = useRemoveSecondFactor();

  const windowOpen =
    secondFactor.expires_at !== null &&
    Date.parse(secondFactor.expires_at) > Date.now();

  if (secondFactor.enrolled && !enrolment) {
    return (
      <section className="section">
        <div className="section__head">
          <h2 className="section__title">Your code</h2>
          <span className="section__meta">
            <IconShieldCheck size={13} aria-hidden="true" /> set up
          </span>
        </div>

        <p className="form__note">
          Four acts ask for it: widening what we may do with a client's data,
          asking for a file to be destroyed, approving somebody's account, and
          minting a key. Adding a file does not — a code prompt on the everyday
          act is how a file ends up staying in Slack.
        </p>

        <p className="form__note">
          {windowOpen
            ? `Confirmed at ${formatClock(secondFactor.confirmed_at)}; you will not be asked again until ${formatClock(secondFactor.expires_at)}.`
            : stepUpMinutes
              ? `You will be asked for it the next time you do one of those four things, and then not again for ${stepUpMinutes} minutes.`
              : "You will be asked for it the next time you do one of those four things."}
        </p>

        {removing ? (
          <form
            className="form"
            onSubmit={(event) => {
              event.preventDefault();
              remove.mutate(
                { code: removeCode },
                {
                  onSuccess: () => {
                    setRemoving(false);
                    setRemoveCode("");
                  },
                },
              );
            }}
          >
            <Field
              label="Type a current code to remove it"
              htmlFor={removeId}
              hint="A recovery code works here too. Removing it takes every recovery code with it."
              error={remove.isError ? errorMessage(remove.error) : undefined}
            >
              <input
                id={removeId}
                className="input input--mono input--narrow"
                value={removeCode}
                onChange={(event) => setRemoveCode(event.target.value)}
                autoComplete="one-time-code"
                inputMode="numeric"
              />
            </Field>
            <div className="gate__actions">
              <button
                type="submit"
                className="btn btn--danger"
                disabled={remove.isPending || removeCode.trim().length === 0}
              >
                {remove.isPending ? "Removing…" : "Remove my code"}
              </button>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setRemoving(false)}
              >
                Keep it
              </button>
            </div>
          </form>
        ) : (
          <button
            type="button"
            className="btn btn--sm"
            onClick={() => setRemoving(true)}
          >
            <IconShieldOff size={13} aria-hidden="true" />
            Remove my code
          </button>
        )}
      </section>
    );
  }

  if (!enrolment) {
    return (
      <section className="section">
        <div className="section__head">
          <h2 className="section__title">Your code</h2>
          <span className="section__meta">not set up yet</span>
        </div>

        <p className="form__note">
          Six digits from your phone, set up once. The console asks for it
          before four things: widening what we may do with a client's data,
          asking for a file to be destroyed, approving somebody's account, and
          minting a key. Adding a file never asks.
        </p>

        {enrol.isError ? (
          <p className="form__error" role="alert">
            {errorMessage(enrol.error)}
          </p>
        ) : null}

        <button
          type="button"
          className="btn btn--primary"
          disabled={enrol.isPending}
          onClick={() =>
            enrol.mutate(undefined, { onSuccess: (data) => setEnrolment(data) })
          }
        >
          {enrol.isPending ? "Setting up…" : "Set up my code"}
        </button>
      </section>
    );
  }

  return (
    <section className="section">
      <div className="section__head">
        <h2 className="section__title">Scan this once</h2>
        <span className="section__meta">shown once, never again</span>
      </div>

      <p className="form__note">
        Point your authenticator app at this square. The console drew it
        itself — nothing about your code has been anywhere near another company.
      </p>

      {/*
        The square is an SVG string authored by this console's own server and
        served over its own origin (`docs/API.md`, second factor). It is
        rendered as markup because that is what it is; no third-party QR
        service and no client library ever sees the secret, which is the whole
        reason the server draws it.
      */}
      <div
        className="qr"
        role="img"
        aria-label="The square your authenticator app reads"
        dangerouslySetInnerHTML={{ __html: enrolment.qr_svg }}
      />

      <details className="form__more">
        <summary>The app cannot scan it</summary>
        <p className="form__note mono">{enrolment.otpauth_uri}</p>
      </details>

      <RecoveryCodes enrolment={enrolment} />

      <form
        className="form"
        onSubmit={(event) => {
          event.preventDefault();
          verify.mutate(
            { code },
            {
              onSuccess: () => {
                setEnrolment(null);
                setCode("");
              },
            },
          );
        }}
      >
        <Field
          label="Now type the six digits it shows"
          htmlFor={codeId}
          hint="Spaces do not matter. Your code is not in force until this works."
          error={verify.isError ? errorMessage(verify.error) : undefined}
        >
          <input
            id={codeId}
            className="input input--mono input--narrow"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            autoComplete="one-time-code"
            inputMode="numeric"
          />
        </Field>
        <div className="gate__actions">
          <button
            type="submit"
            className="btn btn--primary"
            disabled={verify.isPending || code.trim().length === 0}
          >
            {verify.isPending ? "Checking…" : "Confirm"}
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => setEnrolment(null)}
          >
            Not now
          </button>
        </div>
      </form>
    </section>
  );
}
